<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-list"></i>
          ລາຍການເຄື່ອນໄຫວກະເປົ໋າ (Monitor)
          <span class="countTotal">({{ pagination.total }})</span>
        </h4>
        <vs-button flat size="small" @click="exportExcel" :loading="isExporting" :disabled="isExporting">
          <i class="fal fa-file-export"></i> Export Excel
        </vs-button>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns is-multiline">
          <div class="column is-2">
            <label class="label">ເລກບິນ</label>
            <input type="text" class="input" v-model="filters.bill" placeholder="ຄົ້ນຫາເລກບິນ" @keyup.enter="search" />
          </div>
          <div class="column is-2">
            <label class="label">ສາຂາ</label>
            <BranchSelect v-model="filters.branch_id" placeholder="ທັງໝົດ" @input="search" />
          </div>
          <div class="column is-2">
            <label class="label">ປະເພດກະເປົ໋າ</label>
            <div class="select is-fullwidth">
              <select v-model="filters.wallet_type" @change="search">
                <option value="">ທັງໝົດ</option>
                <option value="1">ຄ່າຂົນສົ່ງ</option>
                <option value="2">COD</option>
              </select>
            </div>
          </div>
          <div class="column is-2">
            <label class="label">ປະເພດ</label>
            <div class="select is-fullwidth">
              <select v-model="filters.transaction_type" @change="search">
                <option value="">ທັງໝົດ</option>
                <option value="1">ຫັກ (Charge)</option>
                <option value="2">ຄືນ (Refund)</option>
              </select>
            </div>
          </div>
          <div class="column is-2">
            <label class="label">ເຫດການ</label>
            <div class="select is-fullwidth">
              <select v-model="filters.event_type" @change="search">
                <option value="">ທັງໝົດ</option>
                <option v-for="(label, key) in eventLabels" :key="key" :value="key">{{ label }}</option>
              </select>
            </div>
          </div>
          <div class="column is-2">
            <label class="label">ແຕ່ວັນທີ</label>
            <input type="date" class="input" v-model="filters.from_date" @change="search" />
          </div>
          <div class="column is-2">
            <label class="label">ຫາວັນທີ</label>
            <input type="date" class="input" v-model="filters.to_date" @change="search" />
          </div>
          <div class="column is-2 apply-col">
            <vs-button @click="search" :loading="isLoading"><i class="fal fa-search"></i> ຄົ້ນຫາ</vs-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="box notification is-warning">
        <p>{{ error }}</p>
        <p v-if="errorRequestId" class="error-ref">ref: {{ errorRequestId }}</p>
        <vs-button @click="fetchTransactions" class="mt-3"><i class="fal fa-redo"></i> ລອງໃໝ່</vs-button>
      </div>

      <!-- Filter-aware money total (in=ຄືນ / out=ຫັກ; degrades to nothing when the BFF omits `totals`). -->
      <WalletListTotals :totals="totals" />

      <div class="center">
        <vs-table>
          <template #thead>
            <vs-tr>
              <vs-th>ວັນທີ</vs-th>
              <vs-th>ເລກບິນ</vs-th>
              <vs-th>ກະເປົ໋າ</vs-th>
              <vs-th>ປະເພດ</vs-th>
              <vs-th>ເຫດການ</vs-th>
              <vs-th>ຈຳນວນ</vs-th>
              <vs-th>ການຈ່າຍ</vs-th>
              <vs-th>ສຳເລັດ</vs-th>
              <vs-th>Pre-Order</vs-th>
              <vs-th>ສາຂາ (ຕົ້ນ→ປາຍ)</vs-th>
              <vs-th>ຈັດການ</vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <vs-tr v-for="tx in transactions" :key="tx.id">
              <vs-td>{{ formatMonitorDateTime(tx.created_at) }}</vs-td>
              <vs-td><span class="reference">{{ tx.shipment_number }}</span></vs-td>
              <vs-td><span class="wallet-type" :class="walletClass(tx.wallet_type)">{{ walletLabel(tx.wallet_type) }}</span></vs-td>
              <vs-td>
                <span class="tx-type" :class="isRefund(tx) ? 'credit' : 'debit'">{{ isRefund(tx) ? 'ຄືນ' : 'ຫັກ' }}</span>
              </vs-td>
              <vs-td><small>{{ eventLabel(tx.event_type) }}</small></vs-td>
              <vs-td>
                <span class="tx-amount" :class="isRefund(tx) ? 'credit' : 'debit'">
                  {{ isRefund(tx) ? '+' : '-' }}{{ formatCurrency(tx.amount) }}
                </span>
              </vs-td>
              <vs-td><small>{{ payTypeLabel(tx.shipment_pay_type) }}</small></vs-td>
              <vs-td><small>{{ successLabel(tx.shipment_success_type) }}</small></vs-td>
              <vs-td>{{ preOrderLabel(tx.shipment_is_pre_order) }}</vs-td>
              <vs-td><small>{{ branchName(tx.start_branch_id, tx.start_branch_prefix, tx.start_branch_name) }} → {{ branchName(tx.end_branch_id, tx.end_branch_prefix, tx.end_branch_name) }}</small></vs-td>
              <vs-td>
                <vs-button flat icon size="small" @click="openDrill(tx.shipment_number)" title="ເບິ່ງລາຍລະອຽດ">
                  <i class="fal fa-search-plus" style="color:#3273dc"></i>
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
          <template #notFound>
            <div class="not-found">ບໍ່ພົບລາຍການ</div>
          </template>
        </vs-table>

        <vs-pagination v-if="pagination.last_page > 1" v-model="page" :length="pagination.last_page" @input="fetchTransactions" />
      </div>
    </div>

    <!-- Per-shipment drill-down -->
    <vs-dialog v-model="showDrill" width="720px">
      <template #header>
        <h4 class="dialog-title"><i class="fal fa-receipt"></i> ລາຍລະອຽດບິນ {{ drillBill }}</h4>
      </template>
      <div class="drill-content">
        <div v-if="drillLoading" class="has-text-centered py-5"><i class="fal fa-spinner fa-spin fa-2x"></i></div>
        <template v-else-if="drill">
          <!-- Per-wallet net: FREIGHT and COD are reported separately, never summed. -->
          <div class="columns">
            <div class="column is-6"><div class="net-card freight">
              <span class="tag is-info">ຄ່າຂົນສົ່ງ</span> ສຸດທິ <strong>{{ formatCurrency(drill.net_by_wallet && drill.net_by_wallet.freight) }}</strong>
            </div></div>
            <div class="column is-6"><div class="net-card cod">
              <span class="tag is-warning">COD</span> ສຸດທິ <strong>{{ formatCurrency(drill.net_by_wallet && drill.net_by_wallet.cod) }}</strong>
            </div></div>
          </div>

          <div class="info-grid">
            <div><span class="info-label">ການຈ່າຍ</span><span>{{ payTypeLabel(drillShipment.shipment_pay_type) }}</span></div>
            <div><span class="info-label">ສຳເລັດ</span><span>{{ successLabel(drillShipment.shipment_success_type) }}</span></div>
            <div><span class="info-label">Pre-Order</span><span>{{ preOrderLabel(drillShipment.shipment_is_pre_order) }}</span></div>
            <div><span class="info-label">ສາຂາ</span><span>{{ branchName(drillShipment.start_branch_id, drillShipment.start_branch_prefix, drillShipment.start_branch_name) }} → {{ branchName(drillShipment.end_branch_id, drillShipment.end_branch_prefix, drillShipment.end_branch_name) }}</span></div>
            <div><span class="info-label">ສ້າງເມື່ອ</span><span>{{ formatMonitorDateTime(drillShipment.created_at) }}</span></div>
          </div>

          <h5 class="section-title">ການເຄື່ອນໄຫວ</h5>
          <table class="table is-fullwidth is-striped is-narrow">
            <thead><tr><th>ວັນທີ</th><th>ກະເປົ໋າ</th><th>ເຫດການ</th><th>ປະເພດ</th><th class="num">ຈຳນວນ</th></tr></thead>
            <tbody>
              <tr v-for="r in (drill.transactions || [])" :key="r.id">
                <td><small>{{ formatMonitorDateTime(r.created_at) }}</small></td>
                <td>{{ walletLabel(r.wallet_type) }}</td>
                <td><small>{{ eventLabel(r.event_type) }}</small></td>
                <td><span :class="isRefund(r) ? 'has-text-success' : 'has-text-danger'">{{ isRefund(r) ? 'ຄືນ' : 'ຫັກ' }}</span></td>
                <td class="num">
                  <span :class="isRefund(r) ? 'has-text-success' : 'has-text-danger'">
                    {{ isRefund(r) ? '+' : '-' }}{{ formatCurrency(r.amount) }}
                  </span>
                  <span v-if="r.wallet_transaction_id == null" class="tag is-light ml">ລໍຖ້າ</span>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
        <div v-else class="has-text-centered py-5 has-text-grey">ບໍ່ພົບຂໍ້ມູນ</div>
      </div>
      <template #footer>
        <div class="dialog-footer"><vs-button flat @click="showDrill = false">ປິດ</vs-button></div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import walletMonitorService from '@/services/walletMonitorService';
import { extractErrorInfo, fetchWalletBranches } from '@/services/walletService';
import { formatCurrency } from '@/services/walletFormatters';
import {
  EVENT_LABELS, eventLabel, payTypeLabel, walletLabel, walletClass, preOrderLabel, successLabel,
  branchLabel, formatMonitorDateTime, todayStr,
} from '@/services/walletMonitorLabels';
import WalletListTotals from '../WalletListTotals.vue';
import BranchSelect from '@/components/BranchSelect.vue';

// Async export tuning — mirrors Monitor/Export.vue (the BFF caps the range at 92 days).
const EXPORT_REFRESH_MS = 6000;
const EXPORT_MAX_POLLS = 40;      // ~4 min of bounded polling, then stop (never loop forever)
const EXPORT_MAX_RANGE_DAYS = 92;

export default {
  name: 'WalletMonitorTransactions',
  components: { WalletListTotals, BranchSelect },
  data() {
    return {
      branches: [],
      transactions: [],
      totals: null,
      pagination: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
      page: 1,
      isLoading: false,
      error: null,
      errorRequestId: null,
      abortController: null,
      eventLabels: EVENT_LABELS,
      filters: { bill: '', branch_id: '', wallet_type: '', transaction_type: '', event_type: '', from_date: todayStr(), to_date: todayStr() },
      showDrill: false,
      drillBill: '',
      drill: null,
      drillLoading: false,
      isExporting: false,
      exportPollTimer: null,
      exportPollCount: 0,
    };
  },
  computed: {
    // Null-safe shipment snapshot for the drill-down dialog (a malformed/empty drill never throws on render).
    drillShipment() {
      return (this.drill && this.drill.shipment) || {};
    },
    // branchId → branch, for resolving raw branch ids to "PREFIX - Name" (falls back to the row's prefix).
    branchMap() {
      const m = {};
      (this.branches || []).forEach((b) => { m[b.id] = b; });
      return m;
    },
  },
  mounted() {
    this.fetchBranches();
  },
  // React to the route query itself (not just on first mount) so filters carried from the Summary total
  // cards always apply — even across the router/sidebar's navigation race. immediate → also drives the
  // initial load (and a direct visit with no query just uses the today defaults).
  watch: {
    '$route.query': {
      immediate: true,
      handler() {
        this.seedFromQuery();
        this.page = 1;
        // Search-driven: a plain open (no query) waits for the ຄົ້ນຫາ button; only auto-load when
        // arriving WITH filters carried in the query (e.g. from a Summary total card).
        if (Object.keys(this.$route.query || {}).length > 0) {
          this.fetchTransactions();
        }
      },
    },
  },
  beforeDestroy() {
    if (this.abortController) this.abortController.abort();
    this.stopExportPoll();
  },
  methods: {
    formatCurrency, formatMonitorDateTime,
    eventLabel, payTypeLabel, walletLabel, walletClass, preOrderLabel, successLabel,
    isRefund(tx) { return Number(tx.transaction_type) === 2; },
    branchName(id, prefix, name) { return branchLabel(this.branchMap, id, prefix, name); },
    // Pre-fill filters from query params (e.g. when arriving from a Summary total card). branch_id is
    // coerced to a Number so the <select> (whose option :value is the numeric b.id) actually shows it.
    seedFromQuery() {
      const q = this.$route.query || {};
      ['wallet_type', 'branch_id', 'from_date', 'to_date', 'bill', 'event_type', 'transaction_type'].forEach((k) => {
        if (q[k] === undefined || q[k] === null || q[k] === '') return;
        if (k === 'branch_id') {
          const n = Number(q[k]);
          this.filters.branch_id = Number.isNaN(n) ? '' : n;
        } else {
          this.filters[k] = String(q[k]);
        }
      });
    },
    async fetchBranches() {
      try { this.branches = await fetchWalletBranches(this.$axios); } catch (e) { /* non-fatal */ }
    },
    search() { this.page = 1; this.fetchTransactions(); },
    async fetchTransactions() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.isLoading = true;
      this.error = null;
      this.errorRequestId = null;
      try {
        const params = { source: 'shipment', page: this.page, per_page: this.pagination.per_page, ...this.filters };
        const res = await walletMonitorService.getTransactions(params, this.abortController.signal);
        const d = res.data || res;
        this.transactions = d.transactions || [];
        if (d.pagination) this.pagination = d.pagination;
        // Totals ride only on page 1 (filter-aware over the whole set); retain them while paging.
        if (this.page === 1) this.totals = d.totals || null;
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        const { message, requestId } = extractErrorInfo(error);
        this.error = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້';
        this.errorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },
    async openDrill(bill) {
      if (!bill) return;
      this.drillBill = bill;
      this.drill = null;
      this.drillLoading = true;
      this.showDrill = true;
      try {
        const res = await walletMonitorService.getShipment(bill);
        this.drill = res.data || res;
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, extractErrorInfo(error).message || 'ບໍ່ສາມາດໂຫລດລາຍລະອຽດ');
        this.showDrill = false;
      } finally {
        this.drillLoading = false;
      }
    },
    // Queue a filter-faithful transactions export and inline-poll until the file is ready, then auto-download.
    // Carries the SAME filters as the on-screen list (the backend builds it from the same reader).
    async exportExcel() {
      const { from_date: from, to_date: to } = this.filters;
      if (!from || !to) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000, 'ກະລຸນາເລືອກໄລຍະວັນທີ');
        return;
      }
      const days = Math.floor((new Date(to) - new Date(from)) / 86400000) + 1;
      if (days < 1 || days > EXPORT_MAX_RANGE_DAYS) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000, `ໄລຍະວັນທີຕ້ອງລະຫວ່າງ 1-${EXPORT_MAX_RANGE_DAYS} ວັນ`);
        return;
      }
      this.stopExportPoll();
      this.exportPollCount = 0;
      this.isExporting = true;
      try {
        const body = await walletMonitorService.requestExport({ report_type: 'transactions', ...this.filters });
        const fileId = Number((body && (body.data || body)).file_id);
        if (!fileId) throw new Error('missing file_id');
        // No "queued" toast — the button spinner shows progress and the file auto-downloads when ready;
        // a success toast here is misleading (nothing is saved yet, the job is only queued).
        this.pollExport(fileId);
      } catch (error) {
        this.isExporting = false;
        // 429 = an export is already in flight for this user+type (one-at-a-time guard).
        const msg = error.response && error.response.status === 429
          ? 'ມີການສົ່ງອອກກຳລັງດຳເນີນຢູ່ ກະລຸນາລໍຖ້າ'
          : (extractErrorInfo(error).message || 'ບໍ່ສາມາດສ້າງການສົ່ງອອກ');
        this.$notification.OpenNotification_Error('top-right', 'danger', 4000, msg);
      }
    },
    pollExport(fileId) {
      this.stopExportPoll();
      // ±20% jitter so concurrent viewers don't poll in lock-step.
      const delay = Math.round(EXPORT_REFRESH_MS * (0.8 + Math.random() * 0.4));
      this.exportPollTimer = setTimeout(async () => {
        this.exportPollCount += 1;
        try {
          const result = await walletMonitorService.downloadExport(fileId);
          if (result && result.ready === false) {
            if (result.status === 'failed') {
              this.isExporting = false;
              this.$notification.OpenNotification_Error('top-right', 'danger', 4000, 'ການສົ່ງອອກລົ້ມເຫລວ');
              return;
            }
            if (this.exportPollCount >= EXPORT_MAX_POLLS) {
              this.isExporting = false;
              this.$notification.OpenNotification_Error('top-right', 'warning', 4000, 'ໃຊ້ເວລາດົນ ກະລຸນາໂຫລດຈາກໜ້າ Export');
              return;
            }
            this.pollExport(fileId); // still processing — keep polling
            return;
          }
          this.isExporting = false; // ready → downloadExport already triggered the browser download
        } catch (error) {
          this.isExporting = false;
          this.$notification.OpenNotification_Error('top-right', 'danger', 3000, extractErrorInfo(error).message || 'ບໍ່ສາມາດໂຫລດໄຟລ໌');
        }
      }, delay);
    },
    stopExportPoll() {
      if (this.exportPollTimer) { clearTimeout(this.exportPollTimer); this.exportPollTimer = null; }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './date-input';
.container { margin-top: 20px; }
.box-title { margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between;
  h4 { font-size: 18px; font-weight: 600; i { margin-right: 8px; color: #3273dc; } } }
.countTotal { color: #666; font-weight: normal; }
.apply-col { display: flex; align-items: flex-end; }
.error-ref { font-size: 12px; color: #856404; margin-top: 4px; }
.mt-3 { margin-top: 1rem; }
.py-5 { padding: 3rem 0; }
.num { text-align: right; }
.ml { margin-left: 6px; }
.reference { font-size: 12px; font-family: monospace; color: #444; }
.wallet-type { padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;
  &.freight { background: #e3f2fd; color: #1565c0; } &.cod { background: #fff3e0; color: #e65100; } }
.tx-type { padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 500;
  &.credit { background: #d4edda; color: #155724; } &.debit { background: #f8d7da; color: #721c24; } }
.tx-amount { font-weight: 600; &.credit { color: #28a745; } &.debit { color: #dc3545; } }
.not-found { text-align: center; padding: 40px; color: #999; }
.dialog-title { font-size: 18px; font-weight: 600; i { margin-right: 8px; color: #3273dc; } }
.dialog-footer { display: flex; justify-content: flex-end; padding: 16px 20px; border-top: 1px solid #eee; }
.drill-content { padding: 16px 20px; }
.net-card { padding: 12px; border-radius: 6px; background: #f8f9fa; border-left: 4px solid #3273dc; strong { margin-left: 6px; }
  &.cod { border-left-color: #f0ad4e; } }
.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin: 16px 0;
  > div { display: flex; justify-content: space-between; } .info-label { color: #666; } }
.section-title { font-size: 15px; font-weight: 600; margin: 12px 0; }
</style>
